import { useCallback, useEffect } from "react";

/**
 * Google reCAPTCHA v3（スコアベース）用フック（§4.1）
 *
 * - マウント時に reCAPTCHA v3 スクリプトを動的ロードする
 *   （サイトキーは VITE_RECAPTCHA_SITE_KEY から取得）。
 * - executeRecaptcha(action) でトークンを取得し、ログイン API へ渡す。
 * - サイトキー未設定時は何もしない（executeRecaptcha は undefined を返す）。
 *   ローカル開発でキー無しでもログインできるようにするため
 *   （BE 側も RECAPTCHA_SECRET_KEY 未設定なら検証スキップ）。
 *
 * v3 はチャレンジ UI を出さず、右下にバッジを表示する。
 */

const SCRIPT_ID = "recaptcha-v3-script";

interface Grecaptcha {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

function getSiteKey(): string {
  // shared-auth は複数の Vite アプリから利用される。import.meta.env は
  // 各アプリのビルド時に解決される。型は環境非依存にするため any 経由で読む。
  const env = (import.meta as unknown as { env?: Record<string, string> }).env;
  return env?.VITE_RECAPTCHA_SITE_KEY ?? "";
}

export function useRecaptcha() {
  const siteKey = getSiteKey();

  useEffect(() => {
    if (!siteKey) return; // キー未設定なら何もしない（検証スキップ）
    if (document.getElementById(SCRIPT_ID)) return; // 二重挿入防止

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
      siteKey
    )}`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, [siteKey]);

  /**
   * reCAPTCHA トークンを取得する。
   * @param action - action 名（BE の期待値と一致させる。ログインは "login"）
   * @returns トークン文字列。キー未設定 or 取得失敗時は undefined
   */
  const executeRecaptcha = useCallback(
    async (action: string): Promise<string | undefined> => {
      if (!siteKey || !window.grecaptcha) return undefined;
      try {
        return await new Promise<string>((resolve, reject) => {
          window.grecaptcha!.ready(() => {
            window
              .grecaptcha!.execute(siteKey, { action })
              .then(resolve)
              .catch(reject);
          });
        });
      } catch (e) {
        console.error("[useRecaptcha] execute failed:", e);
        return undefined;
      }
    },
    [siteKey]
  );

  return { executeRecaptcha };
}
